import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getPlainTextLength, preventOrphan, truncateHtml } from "@SupplyLand/Utilities/richText";
import parse, { domToReact } from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    posts = "posts",
    summaryLength = "summaryLength",
    showDate = "showDate",
    readMoreText = "readMoreText",
    viewAllLink = "viewAllLink",
    viewAllText = "viewAllText",
    customCSS = "customCSS",
}

interface SelectedPostModel extends HasFields {
    fields: {
        page: LinkFieldValue;
        summary?: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.posts]: SelectedPostModel[];
        [fields.summaryLength]: number;
        [fields.showDate]: boolean;
        [fields.readMoreText]: string;
        [fields.viewAllLink]: LinkFieldValue;
        [fields.viewAllText]: string;
        [fields.customCSS]: string;
    };
}

interface BlogPost {
    nodeId: string;
    title: string;
    url: string;
    publishDate?: Date;
    summaryHtml?: string;
}

const toValidDate = (value: string | undefined) => {
    const date = value ? new Date(value) : undefined;
    return date && !Number.isNaN(date.getTime()) ? date : undefined;
};

const escapeHtml = (value: string) =>
    value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const posts: BlogPost[] = [];
    const parentsToLoad = new Set<string>();

    // Posts render in the order the editor arranged them in the CMS
    for (const selected of ownProps.fields.posts ?? []) {
        const destination = selected.fields?.page;
        if (destination?.type !== "Page" || !destination.value) {
            continue;
        }

        const link = getPageLinkByNodeId(state, destination.value);
        if (!link?.url) {
            // Unpublished, deleted, or hidden from this visitor
            continue;
        }

        // The post's own fields (summary, news publish date) come from loading its parent's children
        let page: PageProps | undefined;
        if (link.parentId) {
            const siblings = getDataView(state.data.pages, { parentNodeId: link.parentId });
            if (siblings.value) {
                page = (siblings.value as PageProps[]).find(o => o.nodeId === destination.value);
            } else if (!siblings.isLoading) {
                parentsToLoad.add(link.parentId);
            }
        }

        const summaryOverride = selected.fields.summary?.trim();
        posts.push({
            nodeId: destination.value,
            title: page?.fields["title"] || link.title,
            url: link.url,
            publishDate: toValidDate(page?.fields["publishDate"]),
            summaryHtml: summaryOverride
                ? `<p>${escapeHtml(summaryOverride)}</p>`
                : page?.fields["newsSummary"] || page?.fields["metaDescription"] || page?.fields["newsContent"] || "",
        });
    }

    const viewAllLink =
        ownProps.fields.viewAllLink?.type === "Page"
            ? getPageLinkByNodeId(state, ownProps.fields.viewAllLink.value)?.url
            : ownProps.fields.viewAllLink?.type === "Url"
              ? ownProps.fields.viewAllLink.value
              : undefined;

    return {
        posts,
        parentsToLoad: [...parentsToLoad],
        viewAllLink,
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentBlogPostsStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyProps;
    container?: GridContainerProps;
    postGridItem?: GridItemProps;
    post?: InjectableCss;
    dateText?: TypographyProps;
    postTitleText?: TypographyProps;
    postTitleLink?: LinkPresentationProps;
    summaryWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    viewAllWrapper?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
}

export const recentBlogPostsStyles: RecentBlogPostsStyles = {
    wrapper: {
        css: css`
            width: 100%;
            padding: 40px 0;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            text-transform: uppercase;
            font-size: 28px;
            font-weight: 700;
            line-height: 1.2;
            margin-bottom: 30px;
        `,
    },
    container: {
        gap: 30,
    },
    postGridItem: {
        width: [12, 12, 6, 4, 4],
    },
    post: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 100%;
            border-top: 4px solid #ffd100;
            padding-top: 15px;
        `,
    },
    dateText: {
        css: css`
            font-size: 13px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin-bottom: 8px;
        `,
    },
    postTitleText: {
        variant: "h3",
        css: css`
            font-size: 20px;
            font-weight: 700;
            line-height: 1.25;
            margin-bottom: 10px;
        `,
    },
    postTitleLink: {
        color: "text.main",
        css: css`
            text-decoration: none;
            &:hover {
                text-decoration: underline;
            }
        `,
    },
    summaryWrapper: {
        css: css`
            flex: 1 1 auto;
            font-size: 15px;
            font-weight: 500;
            line-height: 1.5;
            margin-bottom: 15px;
            p {
                margin: 0 0 10px;
            }
            * {
                font-family: inherit !important;
                font-size: inherit !important;
            }
        `,
    },
    readMoreLink: {
        css: css`
            font-weight: 700;
            text-transform: uppercase;
            color: #000;
        `,
    },
    viewAllWrapper: {
        css: css`
            text-align: center;
            margin-top: 30px;
        `,
    },
    viewAllLink: {
        css: css`
            font-weight: 700;
            text-transform: uppercase;
            color: #000;
        `,
    },
};

const styles = recentBlogPostsStyles;

/**
 * Features blog posts hand-picked in the CMS (in the order the editor arranges them) on the home page.
 * Renders server-side so crawlers and LLMs see the content.
 */
class RecentBlogPosts extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        this.loadPostPages();
    }

    componentDidUpdate() {
        this.loadPostPages();
    }

    loadPostPages() {
        for (const parentNodeId of this.props.parentsToLoad) {
            this.props.loadPagesForParent({ parentNodeId });
        }
    }

    renderSummary(post: BlogPost) {
        const { summaryLength } = this.props.fields;
        if (!post.summaryHtml) {
            return null;
        }

        const limit = summaryLength > 0 ? summaryLength : 200;
        const content =
            getPlainTextLength(post.summaryHtml) > limit
                ? domToReact(truncateHtml(post.summaryHtml, limit), parserOptions)
                : parse(post.summaryHtml, parserOptions);

        return (
            <StyledWrapper {...styles.summaryWrapper} data-test-selector="recentBlogPosts_summary">
                {content}
            </StyledWrapper>
        );
    }

    render() {
        const { posts, viewAllLink, fields } = this.props;
        const { title, showDate, readMoreText, viewAllText, customCSS } = fields;

        if (posts.length === 0) {
            return null;
        }

        const customCssWrapper = {
            css: css`
                ${customCSS}
            `,
        };

        return (
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper {...styles.wrapper} className="recent-blog-posts" data-test-selector="recentBlogPosts">
                    {title && (
                        <Typography {...styles.titleText} className="recent-blog-posts-title">
                            {preventOrphan(title)}
                        </Typography>
                    )}
                    <GridContainer {...styles.container}>
                        {posts.map(post => (
                            <GridItem key={post.nodeId} {...styles.postGridItem}>
                                <StyledWrapper
                                    as="article"
                                    {...styles.post}
                                    className="recent-blog-post"
                                    data-test-selector={`recentBlogPosts_post_${post.nodeId}`}
                                >
                                    {showDate && post.publishDate && (
                                        <Typography {...styles.dateText} className="recent-blog-post-date">
                                            <LocalizedDateTime
                                                dateTime={post.publishDate}
                                                options={{ year: "numeric", month: "long", day: "numeric" }}
                                            />
                                        </Typography>
                                    )}
                                    <Typography {...styles.postTitleText} className="recent-blog-post-title">
                                        <Link href={post.url} {...styles.postTitleLink}>
                                            {preventOrphan(post.title)}
                                        </Link>
                                    </Typography>
                                    {this.renderSummary(post)}
                                    <Link
                                        href={post.url}
                                        {...styles.readMoreLink}
                                        className="recent-blog-post-read-more"
                                    >
                                        {readMoreText || "Read More"}
                                    </Link>
                                </StyledWrapper>
                            </GridItem>
                        ))}
                    </GridContainer>
                    {viewAllLink && (
                        <StyledWrapper {...styles.viewAllWrapper}>
                            <Link href={viewAllLink} {...styles.viewAllLink} className="recent-blog-posts-view-all">
                                {viewAllText || "View All Posts"}
                            </Link>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentBlogPosts),
    definition: {
        group: "Basic",
        displayName: "Recent Blog Posts",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "From the Blog",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.posts,
                displayName: "Blog Posts (shown in this order)",
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                getDisplay: (item: HasFields, state) => {
                    const page: LinkFieldValue | undefined = item.fields?.page;
                    if (!page?.value) {
                        return "Select a blog post";
                    }
                    return getPageLinkByNodeId(state, page.value)?.title ?? "Unknown page";
                },
                fieldDefinitions: [
                    {
                        name: "page",
                        displayName: "Blog Post Page",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        allowUrls: () => false,
                        allowCategories: () => false,
                        isRequired: true,
                    },
                    {
                        name: "summary",
                        displayName: "Summary (optional, defaults to the page's meta description)",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.summaryLength,
                displayName: "Summary Length (characters)",
                editorTemplate: "IntegerField",
                defaultValue: 200,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showDate,
                displayName: "Show Publish Date (News pages only)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.readMoreText,
                displayName: "Read More Text",
                editorTemplate: "TextField",
                defaultValue: "Read More",
                fieldType: "Translatable",
                sortOrder: 5,
            },
            {
                name: fields.viewAllLink,
                displayName: "View All Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.viewAllText,
                displayName: "View All Text",
                editorTemplate: "TextField",
                defaultValue: "View All Posts",
                fieldType: "Translatable",
                sortOrder: 7,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 8,
            },
        ],
    },
};

export default widgetModule;
